import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ownerImage from './ANN.jpg';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const About = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700 py-24">
        {/* Background pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)',
          }}></div>
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative container mx-auto px-4">
          <div className="flex flex-col items-center justify-center text-center space-y-6">
            <div className="inline-flex items-center gap-2 text-white/90 font-medium">
              <span className="text-sm uppercase tracking-wider">About Us</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white font-serif">
              The Oceanique <span className="text-cyan-300">Story</span>
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl">
              Where innovation meets tranquility in the world of medical aesthetics
            </p>
          </div>
        </div>
        

      </div>

      <div className="container mx-auto px-4 py-12 -mt-8 relative z-10">
        {/* Meet the Owner Section */}
        <section className="mb-16">
          <Card className="overflow-hidden shadow-xl border-0">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-8 lg:p-12">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-2xl blur-2xl opacity-30"></div>
                    <img 
                      src={ownerImage} 
                      alt="Ann Rivera" 
                      className="relative w-full max-w-md mx-auto rounded-2xl shadow-2xl object-cover"
                    />
                  </div>
                  <div className="space-y-6">
                    <div>
                      <Badge className="mb-4 bg-blue-600 text-white">Founder & CEO</Badge>
                      <h2 className="text-4xl font-bold font-serif mb-2">Ann Rivera</h2>
                      <p className="text-xl text-gray-600">Master Medical Aesthetician</p>
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Ann founded Oceanique Medical Spa with a revolutionary vision: to create a sanctuary where 
                      advanced medical aesthetics meets the calming essence of the ocean. With over a decade of 
                      experience and extensive training in the latest aesthetic technologies, Ann has transformed 
                      countless lives through her expertise in laser treatments, body contouring, and skin rejuvenation.
                    </p>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Her commitment to excellence and personalized care has made Oceanique the premier destination 
                      for those seeking transformative aesthetic treatments in a luxurious, ocean-inspired environment.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2 text-blue-600">
                        <span className="text-sm font-medium">Certified Expert</span>
                      </div>
                      <div className="flex items-center gap-2 text-blue-600">
                        <span className="text-sm font-medium">10+ Years Experience</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Our Story Section */}
        <section className="mb-16">
          <div className="text-center mb-12 space-y-4">
            <Badge variant="secondary" className="bg-blue-100 text-blue-700">Our Journey</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold font-serif">
              <span className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent bg-size-200 animate-gradient">
                The Evolution of Excellence
              </span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <CardTitle className="text-2xl font-serif text-blue-700">Our Beginning</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Established in 2021, Oceanique emerged from a desire to revolutionize the medical spa experience. 
                  We recognized the need for a space that combined cutting-edge aesthetic technology with the 
                  healing tranquility of the ocean, creating an environment where transformation happens naturally.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardHeader>
                <CardTitle className="text-2xl font-serif text-blue-700">Our Growth</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Today, Oceanique stands as a beacon of innovation in medical aesthetics. Our continuous investment 
                  in the latest technologies and our team's ongoing education ensures that our clients receive 
                  treatments that are not just effective, but truly transformative.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Mission & Values Section */}
        <section className="mb-16">
          <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-0 shadow-xl">
            <CardHeader className="text-center">
              <CardTitle className="text-4xl font-serif text-blue-800">Our Mission & Values</CardTitle>
              <p className="text-lg text-gray-600 mt-2">Guiding principles that define our excellence</p>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white">
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Personalized Care</h3>
                  <p className="text-gray-600">Every treatment is tailored to your unique needs and aesthetic goals</p>
                </div>
                
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white">
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Safety First</h3>
                  <p className="text-gray-600">Highest standards of safety and medical ethics in every procedure</p>
                </div>
                
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white">
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Innovation</h3>
                  <p className="text-gray-600">Cutting-edge technology and the latest advancement in aesthetics</p>
                </div>
                
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white">
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Community</h3>
                  <p className="text-gray-600">Building lasting relationships with our clients and community</p>
                </div>
                
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white">
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Tranquility</h3>
                  <p className="text-gray-600">Creating a serene environment for your transformation journey</p>
                </div>
                
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white">
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Excellence</h3>
                  <p className="text-gray-600">Committed to delivering exceptional results every time</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Why Choose Us Section */}
        <section className="text-center py-12">
          <h2 className="text-3xl sm:text-4xl font-bold font-serif mb-6">
            Why Choose <span className="text-blue-600">Oceanique</span>?
          </h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8">
            Experience the perfect harmony of advanced medical aesthetics and ocean-inspired tranquility. 
            At Oceanique, your transformation is our passion, and your satisfaction is our promise.
          </p>
          <a 
            href="/booking" 
            className="inline-block bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105"
          >
            Begin Your Journey
          </a>
        </section>
      </div>


    </div>
  );
};

export default About;